import { For, Show, createEffect, createMemo, type Component } from 'solid-js';

import type { GameStore } from '../state/store';
import {
  entityBlips,
  landmarkBlips,
  onMap,
  projectToMap,
  tileClass,
  tileQuad,
  type Blip,
  type MapBox,
} from './minimap';

/**
 * The minimap: the zone's shape in a corner, with everything on it that the player can
 * already see.
 *
 * ── Two layers, for two different reasons ────────────────────────────────────
 *
 * **Terrain is a `<canvas>`**, painted once per zone. A 30 × 30 zone is nine hundred
 * tiles, and nine hundred DOM nodes that never change is a great deal of layout for a box
 * the size of a postage stamp. It is repainted from an effect keyed on the zone, so it
 * costs nothing per frame.
 *
 * **Blips are DOM**, positioned per frame. There are a dozen of them, they change every
 * tick, and being real elements is what makes them testable: jsdom gives no canvas
 * context, so anything painted would be invisible to the vitest suite. The split is
 * therefore not only a performance one — it puts the part that can be *wrong* (where a
 * monster is) where a test can see it, and the part that is merely expensive (nine hundred
 * squares) where it is cheap.
 *
 * `getContext('2d')` returns null under jsdom and the paint is skipped, which is why the
 * effect guards rather than assumes. That is a real environment, not a defensive nicety.
 *
 * ── What it may show ────────────────────────────────────────────────────────
 *
 * Only what the client already holds. A dormant pack is absent from the snapshot — that is
 * how an ambush stays an ambush — so the minimap cannot leak one, and no filtering is
 * applied on top: hiding a monster that is visibly on screen would be worse than showing
 * one that is not.
 */

/** The map's pixel box. Fixed rather than measured, so nothing here reads layout. */
const BOX: MapBox = { width: 148, height: 148 };

export const Minimap: Component<{ store: GameStore }> = (props) => {
  const zone = () => props.store.zone();
  let canvas: HTMLCanvasElement | undefined;

  /**
   * Landmarks change with the zone; entities change every tick. Two memos rather than
   * one, so the layout half is not recomputed thirty times a second.
   */
  const landmarks = createMemo<Blip[]>(() => {
    const z = zone();
    return z ? landmarkBlips(z, BOX) : [];
  });

  const blips = createMemo<Blip[]>(() => {
    const z = zone();
    if (!z) return [];
    return entityBlips(z, BOX, props.store.entities(), props.store.selfId()).filter((b) =>
      onMap(BOX, b.at),
    );
  });

  /** The open portal, when there is one — the way home, and worth its own colour. */
  const portals = createMemo<Blip[]>(() => {
    const z = zone();
    if (!z) return [];
    return props.store
      .portals()
      .map((p) => ({ id: p.id, kind: 'portal' as const, at: projectToMap(z, BOX, p) }))
      .filter((b) => onMap(BOX, b.at));
  });

  // Terrain, repainted only when the zone changes.
  createEffect(() => {
    const z = zone();
    if (!canvas || !z) return;
    // jsdom has no 2D context. The map is decoration over information that is also in
    // the blips, so skipping the paint degrades rather than breaks.
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, BOX.width, BOX.height);

    for (const tile of z.tiles) {
      const paint = tileClass(tile.kind);
      if (paint === null) continue;
      // Four projected corners, not a `fillRect`. The map is rotated into screen space, so
      // an upright square drawn at a rotated centre would leave a diagonal gap against
      // every neighbour and the floor would read as hatching. `tileQuad` goes through the
      // same `projectToMap` the blips do, which is what keeps a monster standing on the
      // floor it is actually on.
      const quad = tileQuad(z, BOX, tile);
      ctx.fillStyle =
        paint === 'solid' ? 'rgba(58, 45, 22, 0.85)' : 'rgba(168, 134, 63, 0.28)';
      ctx.beginPath();
      ctx.moveTo(quad[0]!.x, quad[0]!.y);
      for (const point of quad.slice(1)) ctx.lineTo(point.x, point.y);
      ctx.closePath();
      ctx.fill();
      // Painted again as a hairline stroke, because adjacent quads share an edge and
      // canvas antialiasing leaves a seam of background between them otherwise.
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  });

  return (
    <Show when={zone()}>
      {(z) => (
        <div
          class="minimap"
          data-testid="minimap"
          aria-label={`Map of ${z().kind === 'HUB' ? 'town' : 'the zone'}`}
          style={{ '--map-w': `${BOX.width}px`, '--map-h': `${BOX.height}px` }}
        >
          <canvas
            ref={canvas}
            class="minimap__terrain"
            width={BOX.width}
            height={BOX.height}
            data-testid="minimap-terrain"
            aria-hidden="true"
          />

          {/* Landmarks under the living things: a monster standing on the exit matters
              more than the exit does. */}
          <For each={[...landmarks(), ...portals(), ...blips()]}>
            {(blip) => (
              <span
                class={`minimap__blip minimap__blip--${blip.kind}`}
                data-testid={`minimap-blip-${blip.id}`}
                data-kind={blip.kind}
                style={{ left: `${blip.at.x}px`, top: `${blip.at.y}px` }}
              />
            )}
          </For>
        </div>
      )}
    </Show>
  );
};
